import React from "react";

const BLOCK_SEPARATOR_RE = /^\s*(?:---+|\*\*\*+|___+)\s*$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const ORDERED_LIST_RE = /^(\d+)[.)]\s+(.*)$/;
const UNORDERED_LIST_RE = /^[-*+]\s+(.*)$/;
const TABLE_SEPARATOR_RE = /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/;

const normalizeText = (value) => String(value || "").replace(/\r\n/g, "\n");

const splitTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isTableSeparator = (line) => TABLE_SEPARATOR_RE.test(line || "");

const isBlockStarter = (lines, index) => {
  const line = lines[index];
  const nextLine = lines[index + 1] || "";

  if (!line || !line.trim()) {
    return false;
  }

  if (line.trim().startsWith("```")) {
    return true;
  }

  if (HEADING_RE.test(line)) {
    return true;
  }

  if (BLOCK_SEPARATOR_RE.test(line)) {
    return true;
  }

  if (line.trim().startsWith(">")) {
    return true;
  }

  if (ORDERED_LIST_RE.test(line) || UNORDERED_LIST_RE.test(line)) {
    return true;
  }

  return line.includes("|") && isTableSeparator(nextLine);
};

const tokenizeInline = (text, keyPrefix) => {
  const source = String(text || "");
  const nodes = [];
  let cursor = 0;
  let index = 0;

  const pushText = (segment) => {
    if (segment) {
      nodes.push(segment);
    }
  };

  while (cursor < source.length) {
    const remaining = source.slice(cursor);
    const match = remaining.match(
      /(`[^`]+`|\[[^\]]+\]\([^)]+\)|\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_)/
    );

    if (!match) {
      pushText(remaining);
      break;
    }

    const token = match[0];
    const start = match.index || 0;

    if (start > 0) {
      pushText(remaining.slice(0, start));
    }

    const tokenKey = `${keyPrefix}-${index}`;
    index += 1;

    if (token.startsWith("`")) {
      nodes.push(<code key={tokenKey}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("[")) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const href = linkMatch[2].trim();
        const safeHref = /^(https?:|mailto:|tel:|\/|#)/i.test(href) ? href : undefined;

        nodes.push(
          <a
            key={tokenKey}
            href={safeHref}
            target={safeHref && safeHref.indexOf("http") === 0 ? "_blank" : undefined}
            rel={safeHref && safeHref.indexOf("http") === 0 ? "noreferrer" : undefined}
          >
            {tokenizeInline(linkMatch[1], `${tokenKey}-label`)}
          </a>
        );
      } else {
        pushText(token);
      }
    } else if (token.startsWith("**") || token.startsWith("__")) {
      nodes.push(
        <strong key={tokenKey}>
          {tokenizeInline(token.slice(2, -2), `${tokenKey}-strong`)}
        </strong>
      );
    } else if (token.startsWith("*") || token.startsWith("_")) {
      nodes.push(
        <em key={tokenKey}>
          {tokenizeInline(token.slice(1, -1), `${tokenKey}-em`)}
        </em>
      );
    } else {
      pushText(token);
    }

    cursor += start + token.length;
  }

  return nodes;
};

const parseBlocks = (text) => {
  const lines = normalizeText(text).split("\n");
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith("```")) {
      const language = trimmed.replace(/^```+/, "").trim();
      const codeLines = [];
      index += 1;

      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }

      if (index < lines.length) {
        index += 1;
      }

      blocks.push({
        type: "code",
        language,
        content: codeLines.join("\n"),
      });
      continue;
    }

    const headingMatch = line.match(HEADING_RE);
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        content: headingMatch[2].trim(),
      });
      index += 1;
      continue;
    }

    if (BLOCK_SEPARATOR_RE.test(line)) {
      blocks.push({ type: "hr" });
      index += 1;
      continue;
    }

    if (trimmed.startsWith(">")) {
      const quoteLines = [];
      while (index < lines.length && lines[index].trim().startsWith(">")) {
        quoteLines.push(lines[index].replace(/^\s*>\s?/, ""));
        index += 1;
      }

      blocks.push({
        type: "blockquote",
        content: quoteLines.join("\n"),
      });
      continue;
    }

    if (line.includes("|") && isTableSeparator(lines[index + 1])) {
      const header = splitTableRow(line);
      const rows = [];
      index += 2;

      while (
        index < lines.length &&
        lines[index].trim() &&
        lines[index].includes("|") &&
        !HEADING_RE.test(lines[index]) &&
        !BLOCK_SEPARATOR_RE.test(lines[index])
      ) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }

      blocks.push({
        type: "table",
        header,
        rows,
      });
      continue;
    }

    const orderedMatch = line.match(ORDERED_LIST_RE);
    const unorderedMatch = line.match(UNORDERED_LIST_RE);

    if (orderedMatch || unorderedMatch) {
      const ordered = Boolean(orderedMatch);
      const items = [];

      while (index < lines.length) {
        const currentLine = lines[index];
        const currentOrdered = currentLine.match(ORDERED_LIST_RE);
        const currentUnordered = currentLine.match(UNORDERED_LIST_RE);

        if (ordered && !currentOrdered) {
          break;
        }

        if (!ordered && !currentUnordered) {
          break;
        }

        items.push((currentOrdered || currentUnordered)[2] || (currentOrdered || currentUnordered)[1]);
        index += 1;
      }

      blocks.push({
        type: "list",
        ordered,
        items,
      });
      continue;
    }

    const paragraphLines = [trimmed];
    index += 1;

    while (
      index < lines.length &&
      lines[index].trim() &&
      !isBlockStarter(lines, index)
    ) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }

    blocks.push({
      type: "paragraph",
      content: paragraphLines.join(" "),
    });
  }

  return blocks;
};

const renderBlock = (block, index) => {
  const key = `theor-block-${index}`;

  switch (block.type) {
    case "heading": {
      const HeadingTag = `h${block.level}`;
      return <HeadingTag key={key}>{tokenizeInline(block.content, key)}</HeadingTag>;
    }
    case "hr":
      return <hr key={key} />;
    case "blockquote":
      return (
        <blockquote key={key}>
          <p>{tokenizeInline(block.content, key)}</p>
        </blockquote>
      );
    case "code":
      return (
        <pre key={key} data-language={block.language || undefined}>
          <code>{block.content}</code>
        </pre>
      );
    case "table":
      return (
        <table key={key}>
          <thead>
            <tr>
              {block.header.map((cell, cellIndex) => (
                <th key={`${key}-head-${cellIndex}`}>{tokenizeInline(cell, `${key}-head-${cellIndex}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={`${key}-row-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${key}-cell-${rowIndex}-${cellIndex}`}>
                    {tokenizeInline(cell, `${key}-cell-${rowIndex}-${cellIndex}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag key={key}>
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-item-${itemIndex}`}>{tokenizeInline(item, `${key}-item-${itemIndex}`)}</li>
          ))}
        </ListTag>
      );
    }
    case "paragraph":
    default:
      return <p key={key}>{tokenizeInline(block.content, key)}</p>;
  }
};

const Theor = ({ children, className = "" }) => {
  const content = Array.isArray(children)
    ? children.join("")
    : children;

  const text = normalizeText(content);

  if (!text.trim()) {
    return null;
  }

  const blocks = parseBlocks(text);

  return (
    <div className={["theor-markdown", className].filter(Boolean).join(" ")}>
      {blocks.map(renderBlock)}
    </div>
  );
};

export default Theor;
